import {
  Button,
  Content,
  Flex,
  Heading,
  StrokeIcon,
  StrokeIconName,
  usePlatformInfo,
  usePlatformProps,
} from '@acsbe/ui';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from '../../../assets/logo.svg';
import {
  useApplicationViewState,
  useBilling,
  VIEW_STATES,
} from '../../../context';
import { BillingTemplate } from './billing-template';
import { BorderedFlex } from './common';
import { CardDetails } from './payment-details';

export const Billing = () => {
  const { t } = useTranslation();
  const p = usePlatformProps();
  const { uptoDesktopXs } = usePlatformInfo();
  const { setApplicationViewState } = useApplicationViewState();
  const {
    setBillingMethods,
    isNewPaymentMethodAdded,
    setIsNewPaymentMethodAdded,
    setBillingAddress,
    setBillingsCount,
    setBillingAddressLoaded,
  } = useBilling();
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [errorIconName, setErrorIconName] = useState<StrokeIconName>(
    'ExclamationMarkWithCircle',
  );
  const [errorIconScale, setErrorIconScale] = useState<
    'danger' | 'warning' | 'success' | 'base'
  >('danger');

  const getAllBillingMethods = async () => {
    const response = await axios.get('/api/stripe/get-billing-methods', {
      withCredentials: true,
    });
    setBillingMethods(
      Array.isArray(response.data.billings) ? response.data.billings : [],
    );
    setBillingsCount(
      Array.isArray(response.data.billings) ? response.data.billings.length : 0,
    );
  };

  const handleBack = () => {
    setApplicationViewState(VIEW_STATES.DASHBOARD);
  };

  useEffect(() => {
    getAllBillingMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBillingAddress = async () => {
    try {
      setBillingAddressLoaded(false);
      const response = await axios.get('/api/stripe/get-billing-address', {
        withCredentials: true,
      });
      setBillingAddress(response.data?.data);
    } catch (error) {
      setBillingAddress(null);
    } finally {
      setBillingAddressLoaded(true);
    }
  };

  useEffect(() => {
    getBillingAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNewPaymentMethodAdded) {
      getAllBillingMethods();
      setIsNewPaymentMethodAdded(false);
      getBillingAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewPaymentMethodAdded]);

  return (
    <Flex align="center" stretch>
      <BorderedFlex
        setBorderBottom
        scale="ghost"
        paddingInline={8}
        paddingBlock={6}
        align="flex-start"
        self="stretch"
      >
        <Logo aria-label="accessiBe logo" />
      </BorderedFlex>
      <Flex
        paddingInline={35}
        paddingBlock={8}
        scale="ghost"
        align="center"
        gap={8}
        self="stretch"
        stretch
        {...p.uptoDesktop({ paddingInline: 8 })}
        {...p.uptoTablet({ paddingInline: 4, paddingBlock: 6 })}
        {...p.uptoPhablet({ paddingInline: 3, paddingBlock: 6 })}
      >
        <Flex align="flex-start" gap={6} width="100%">
          <Button
            size="lg"
            variant="link"
            scale="primary"
            data-mixpanel="back-billing"
            type="button"
            onClick={handleBack}
          >
            <StrokeIcon name="ArrowLeft" />
            <span>{t('BACK')}</span>
          </Button>
          <Flex justify="center" align="flex-start" gap={1}>
            <Heading
              scale="base"
              size="sm"
              {...p.uptoTablet({ size: 'xxs' })}
              level={1}
              align="center"
              emphasis="low"
              lineHeight={1.25}
            >
              Complete your purchase
            </Heading>
            <Content scale="accent" size="md" emphasis="none" lineHeight={1.5}>
              You're almost done! Review your details and confirm to complete
              your purchase.
            </Content>
          </Flex>
          <Flex
            row={!uptoDesktopXs}
            align="flex-start"
            gap={10}
            self="stretch"
            width="100%"
            {...p.uptoDesktop({ gap: 6 })}
            {...p.uptoDesktopXs({ justify: 'center', align: 'center' })}
          >
            <CardDetails
              paymentError={paymentError}
              setPaymentError={(msg) => {
                setPaymentError(msg);
              }}
              errorIconName={errorIconName}
              errorIconScale={errorIconScale}
            />
            <BillingTemplate
              onPaymentError={(msg, iconName, iconScale) => {
                setPaymentError(msg);
                if (iconName) setErrorIconName(iconName);
                if (iconScale) setErrorIconScale(iconScale);
              }}
              clearPaymentError={() => {
                setPaymentError(null);
              }}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
